import { cn } from '@/lib/utils'
import { getNotificationIcon, timeAgo, type CrmNotification } from '@/lib/notifications'

/**
 * One notification in the bell dropdown or on the Notifications page.
 *
 * Follows the usual inbox convention: unread rows stand out (white, a bar down the left edge,
 * a bold title and a "New" label), read rows recede (soft grey background, muted text). Unread
 * rows used to carry a faint navy tint, which looked grey and so read as "done".
 *
 * The state is never shown by colour alone: unread rows have the visible "New" label and a
 * visually hidden "Unread:" prefix, so screen readers hear it along with the whole row.
 */
export function NotificationRow({
  notification,
  onClick,
  truncate = false,
}: {
  notification: CrmNotification
  onClick: () => void
  truncate?: boolean
}) {
  const unread = !notification.is_read
  return (
    <button
      type="button"
      className={cn(
        'flex w-full items-start gap-3 border-l-[3px] py-3 pl-[13px] pr-4 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        unread ? 'border-l-primary bg-card' : 'border-l-transparent bg-muted/40'
      )}
      onClick={onClick}
    >
      <div className={cn('mt-0.5 flex-shrink-0', !unread && 'opacity-60')}>
        {getNotificationIcon(notification.type)}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm',
            truncate && 'truncate',
            unread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
          )}
        >
          {unread && <span className="sr-only">Unread: </span>}
          {notification.title}
        </p>
        {notification.message && (
          <p
            className={cn(
              'mt-0.5 text-xs',
              truncate && 'truncate',
              unread ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {notification.message}
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">{timeAgo(notification.created_date)}</p>
      </div>
      {unread && (
        <span
          className="mt-0.5 flex-shrink-0 rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold leading-4 text-accent-foreground"
          aria-hidden="true"
        >
          New
        </span>
      )}
    </button>
  )
}
